// XLChip - Sprint 44 XL.
// Chip interactivo para filtros, tags y selecciones. Distinto de XLBadge:
// puede ser clickeable (onClick), llevar icono de cierre (onClose), y tiene
// tamano ligeramente mayor + outline mas presente. Variantes semanticas:
// neutral | info | success | warning | danger.
//
// Uso:
//   <XLChip variant="info">Emitida</XLChip>
//   <XLChip onClick={...} selected>Filtro activo</XLChip>
//   <XLChip onClose={...}>Estado: Borrador</XLChip>
import { useEffect } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';

const CSS_ID = 'xl-chip-css';
const CSS = `
.xl-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: var(--xl-radius-full);
  font-family: var(--xl-font-sans);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  border: 1px solid var(--xl-color-border);
  background: var(--xl-color-surface-alt);
  color: var(--xl-color-text-secondary);
  white-space: nowrap;
  user-select: none;
  transition: background 120ms cubic-bezier(0,0,0.2,1),
              border-color 120ms cubic-bezier(0,0,0.2,1),
              color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-chip--button { cursor: pointer }
.xl-chip--button:hover {
  background: var(--xl-color-surface);
  border-color: rgba(46, 55, 69, 0.25);
  color: var(--xl-color-text);
}
.xl-chip--button:focus-visible {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: 2px;
}

.xl-chip--selected {
  background: rgba(37,99,235,0.10);
  border-color: rgba(37,99,235,0.35);
  color: var(--xl-color-info);
  font-weight: 600;
}

.xl-chip--info    { background: rgba(37,99,235,0.08);  color: var(--xl-color-info);    border-color: rgba(37,99,235,0.22) }
.xl-chip--success { background: rgba(5,150,105,0.08);  color: var(--xl-color-success); border-color: rgba(5,150,105,0.22) }
.xl-chip--warning { background: rgba(217,119,6,0.08);  color: var(--xl-color-warning); border-color: rgba(217,119,6,0.22) }
.xl-chip--danger  { background: rgba(220,38,38,0.08);  color: var(--xl-color-danger);  border-color: rgba(220,38,38,0.22) }

[data-xl-theme="dark"] .xl-chip--selected { background: rgba(96,165,250,0.18); border-color: rgba(96,165,250,0.45) }
[data-xl-theme="dark"] .xl-chip--info    { background: rgba(96,165,250,0.14);  border-color: rgba(96,165,250,0.32) }
[data-xl-theme="dark"] .xl-chip--success { background: rgba(16,185,129,0.14);  border-color: rgba(16,185,129,0.32) }
[data-xl-theme="dark"] .xl-chip--warning { background: rgba(245,158,11,0.14);  border-color: rgba(245,158,11,0.32) }
[data-xl-theme="dark"] .xl-chip--danger  { background: rgba(248,113,113,0.14); border-color: rgba(248,113,113,0.32) }

.xl-chip__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: -4px;
  border: none;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
  border-radius: 50%;
  padding: 0;
  font-family: inherit;
}
.xl-chip__close:hover {
  opacity: 1;
  background: rgba(0,0,0,0.06);
}
[data-xl-theme="dark"] .xl-chip__close:hover { background: rgba(255,255,255,0.1) }
.xl-chip__close:focus-visible {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: 1px;
}
.xl-chip__icon {
  display: inline-flex;
  align-items: center;
  margin-left: -4px;
}
`;

export default function XLChip({
  children,
  variant = 'neutral',
  selected = false,
  icon,
  onClick,
  onClose,
  disabled = false,
  className = '',
  title,
  ariaLabel,
}) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const isButton = typeof onClick === 'function';
  const variantClass = variant !== 'neutral' ? 'xl-chip--' + variant : '';
  const cls = [
    'xl-chip',
    isButton ? 'xl-chip--button' : '',
    selected ? 'xl-chip--selected' : '',
    variantClass,
    className,
  ].filter(Boolean).join(' ');

  const Tag = isButton ? 'button' : 'span';
  const extraProps = isButton
    ? { type: 'button', onClick, disabled, 'aria-pressed': selected || undefined }
    : {};

  return (
    <Tag
      className={cls}
      title={title}
      aria-label={ariaLabel}
      {...extraProps}
    >
      {icon && <span className="xl-chip__icon" aria-hidden="true">{icon}</span>}
      <span>{children}</span>
      {typeof onClose === 'function' && !disabled && (
        <button
          type="button"
          className="xl-chip__close"
          aria-label="Quitar"
          onClick={(e) => { e.stopPropagation(); onClose(e); }}
        >
          {'\u00D7'}
        </button>
      )}
    </Tag>
  );
}
